class TreatmentsForm extends BaseElement
{
  constructor ()
  {
    super();

    this.cacheTime = 300;
    this.customerLocation = {
      latitude: parseFloat(this.querySelector('input[name="default_latitude"]').value),
      longitude: parseFloat(this.querySelector('input[name="default_longitude"]').value)
    };
    this.filteredResults = [];
    this.selectedClinic = null;
    this.selectedTreatments = [];
    this.currentStep = null;
    this.popup = this.closest('custom-treatments-popup');
  }

  init ()
  {
    this.priceMin = this.querySelector('.treatments-form-price-range-input input[name="price_range_min"]');
    this.priceMax = this.querySelector('.treatments-form-price-range-input input[name="price_range_max"]');
    this.priceRange = this.querySelector('.treatments-form-price-range-slider range-slider');

    this.querySelectorAll('.treatments-form-next').forEach(div => {
      const a = div.querySelector('a');

      a.addEventListener('click', e => {
        e.preventDefault();

        if (!div.classList.contains('disabled')) {
          this.goToNextStep();
        }
      });
    });

    this.querySelectorAll('.treatments-form-prev').forEach(div => {
      const a = div.querySelector('a');

      a.addEventListener('click', e => {
        e.preventDefault();

        if (!div.classList.contains('disabled')) {
          this.goToPrevStep();
        }
      });
    });

    this.querySelectorAll('.treatments-form-submit').forEach(div => {
      const a = div.querySelector('a');

      a.addEventListener('click', e => {
        e.preventDefault();

        if (!div.classList.contains('disabled')) {
          if (this.popup) {
            this.popup.close();
          }

          this.submit();
        }
      });
    });

    this.onBroadcast('google_maps_initialized', () => {
      this.onMapInitialized();
    });

    if (this.dataset.loadMap) {
      this.initMaps();
    }

    this.initTypeInputs();
    this.initPriceInputs();
    this.initSlider();
    this.goToStep(1);
    this.loadPrevious();
  }

  refreshStep ()
  {
    let canGoNext = false;
    let canSubmit = false;

    if (this.currentStep === 1) {
      canGoNext = !!this.querySelector('[name="treatment_type"]:checked');
    } else if (this.currentStep === 2) {
      canGoNext = !!this.querySelector('[name="treatment[clinic_id]"]:checked');
    } else if (this.currentStep === 3) {
      canSubmit = !!this.querySelector('[name="treatment_ids[]"]:checked');
    }

    if (canGoNext) {
      this.querySelectorAll('.treatments-form-next').forEach(div => div.classList.remove('disabled'));
    } else {
      this.querySelectorAll('.treatments-form-next').forEach(div => div.classList.add('disabled'));
    }

    if (canSubmit) {
      this.querySelectorAll('.treatments-form-submit').forEach(div => div.classList.remove('disabled'));
    } else {
      this.querySelectorAll('.treatments-form-submit').forEach(div => div.classList.add('disabled'));
    }
  }

  goToStep (step)
  {
    let nextStep = parseInt(step);

    if (nextStep < 1) {
      nextStep = 1;
    } else if (!this.querySelector('.treatments-form-step[data-step="' + step + '"]')) {
      nextStep = this.currentStep;
    }

    this.currentStep = nextStep;
    this.classList.forEach(name => {
      if (name.startsWith('step-')) {
        this.classList.remove(name);
      }
    });
    this.classList.add('step-' + nextStep);

    this.querySelectorAll('[data-step]').forEach(div => {
      const step = div.dataset.step.split(',').map(v => parseInt(v));

      if (step.indexOf(nextStep) > -1) {
        div.classList.add('active');
      } else {
        div.classList.remove('active');
      }
    });

    this.querySelector('.treatments-form-left').scrollTop = 0;

    this.initFilter();
    this.filter();
    this.refreshTreatments();
    this.refreshStep();
  }

  goToNextStep ()
  {
    this.goToStep(this.currentStep + 1);
  }

  goToPrevStep ()
  {
    this.goToStep(this.currentStep - 1);
  }

  initMaps ()
  {
    if (window.treatmentsMapInitialized) {
      return;
    }

    window.treatmentsMapInitialized = true;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://maps.googleapis.com/maps/api/js?key=' + window.googleMaps.apiKey + '&libraries=places,marker&callback=initTreatmentsMap';

    document.head.appendChild(script);

    this.getCustomerCoordinates();
  }

  getCustomerCoordinates ()
  {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        this.customerLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        this.recalcDistance();
        this.recalcInBounds();
      }, error => {
        console.error(error);
      });
    }
  }

  onMapInitialized ()
  {
    this.map = new google.maps.Map(this.querySelector('.treatments-form-map'), {
      zoom: 7,
      center: {
        lat: this.customerLocation.latitude,
        lng: this.customerLocation.longitude
      },
      // This is required by Advanced Markers
      mapId: window.googleMaps.mapId
    });

    this.mapMarkers = [];
    this.mapMarkersByClinicId = {};

    app.clinics.getMapMarkers().then(markers => {
      markers.forEach(data => {
        const marker = new google.maps.marker.AdvancedMarkerElement({
          ...data.marker,
          map: this.map
        });

        marker.dataset.properties = JSON.stringify(data.properties);

        marker.addListener('click', () => {
          if (marker.content.classList.contains('open')) {
            this.closeMarker(marker);
          } else {
            this.openMarker(marker);
          }
        });

        this.mapMarkers.push(marker);
        this.mapMarkersByClinicId[data.properties.id] = marker;
      });

      this.recalcInBounds();
      this.recalcDistance();
      this.filter();
    });

    google.maps.event.addListener(this.map, 'idle', () => {
      this.recalcInBounds();
      this.filter();
    });
  }

  openMarker (marker)
  {
    const clinicId = JSON.parse(marker.dataset.properties).id;

    marker.content.classList.add('open');
    marker.zIndex = 1;
  }

  closeMarker (marker)
  {
    const clinicId = JSON.parse(marker.dataset.properties).id;

    marker.content.classList.remove('open');
    marker.zIndex = null;
  }

  getMapStyles ()
  {
    return [{"featureType":"water","elementType":"geometry","stylers":[{"color":"#e9e9e9"},{"lightness":17}]},{"featureType":"landscape","elementType":"geometry","stylers":[{"color":"#f5f5f5"},{"lightness":20}]},{"featureType":"road.highway","elementType":"geometry.fill","stylers":[{"color":"#ffffff"},{"lightness":17}]},{"featureType":"road.highway","elementType":"geometry.stroke","stylers":[{"color":"#ffffff"},{"lightness":29},{"weight":0.2}]},{"featureType":"road.arterial","elementType":"geometry","stylers":[{"color":"#ffffff"},{"lightness":18}]},{"featureType":"road.local","elementType":"geometry","stylers":[{"color":"#ffffff"},{"lightness":16}]},{"featureType":"poi","elementType":"geometry","stylers":[{"color":"#f5f5f5"},{"lightness":21}]},{"featureType":"poi.park","elementType":"geometry","stylers":[{"color":"#dedede"},{"lightness":21}]},{"elementType":"labels.text.stroke","stylers":[{"visibility":"on"},{"color":"#ffffff"},{"lightness":16}]},{"elementType":"labels.text.fill","stylers":[{"saturation":36},{"color":"#333333"},{"lightness":40}]},{"elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"geometry","stylers":[{"color":"#f2f2f2"},{"lightness":19}]},{"featureType":"administrative","elementType":"geometry.fill","stylers":[{"color":"#fefefe"},{"lightness":20}]},{"featureType":"administrative","elementType":"geometry.stroke","stylers":[{"color":"#fefefe"},{"lightness":17},{"weight":1.2}]}];
  }

  initTypeInputs ()
  {
    this.querySelectorAll('[name="treatment_type"]').forEach(input => {
      input.addEventListener('change', e => {
        this.refreshStep();
      });

      input.addEventListener('input', e => {
        this.refreshStep();
      });
    })
  }
  initPriceInputs ()
  {
    this.priceMin.addEventListener('input', () => {
      this.priceRange.setStart(parseFloat(this.priceMin.value));

      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.filter();
      }, 250);
    });

    this.priceMax.addEventListener('input', () => {
      this.priceRange.setEnd(parseFloat(this.priceMax.value));

      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.filter();
      }, 250);
    });

    this.priceMin.value = this.priceRange.range().start;
    this.priceMax.value = this.priceRange.range().end;
  }

  setPriceMinMax (min, max)
  {
    console.log('settings price min, max, ', min, max);
    this.priceRange.setMinMax(min, max);
  }

  initSlider ()
  {
    this.priceRange.addEventListener('update', e => {
      this.priceMin.value = e.detail.range.start;
      this.priceMax.value = e.detail.range.end;
    });

    this.priceRange.addEventListener('change', e => {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.filter();
      }, 250);
    });
  }

  createClinicElement (clinic)
  {
    const div = document.createElement('label');
    div.classList.add('treatments-form-clinic');
    div.dataset.clinicId = clinic.getId();
    div.for = `treatments-form-clinic-radio-${clinic.getId()}`;
    div.innerHTML = `
      <div class="treatments-form-clinic-radio">
        <div class="custom-radio">
          <input type="radio" name="treatment[clinic_id]" value="${clinic.getId()}" id="treatments-form-clinic-radio-${clinic.getId()}" >
          <span class="custom-radio-status"></span>
          <span class="custom-radio-label"></span>
        </div>
      </div>
      <div class="treatments-form-clinic-head">
        <div class="treatments-form-clinic-title">${clinic.getName()}</div>
        <div class="treatments-form-clinic-distance">${clinic.getDistanceText()}</div>
      </div>
      <div class="treatments-form-clinic-address">${clinic.getAddressString()}</div>
      <div class="treatments-form-clinic-treatments"></div>
    `;

    div.querySelector('input').addEventListener('change', e => {
      if (div.querySelector('input').checked && this.map) {
        const marker = this.mapMarkersByClinicId[clinic.getId()];

        if (marker) {
          this.map.setCenter(marker.position);
          this.openMarker(marker);
        }
      }

      this.selectedClinic = clinic;
      this.refreshStep();
    });

    return div;
  }

  getClinicElement (clinic, treatments)
  {
    const div = this.querySelector(`.treatments-form-clinic[data-clinic-id="${clinic.getId()}"]`);

    if (!div) {
      throw new Error('Could not find clinic element! #' + clinic.getId());
    }

    div.querySelector('.treatments-form-clinic-distance').innerHTML = clinic.getDistanceText();

    const treatmentsDiv = div.querySelector('.treatments-form-clinic-treatments');
    treatmentsDiv.innerHTML = '';

    treatments.forEach(treatment => {
      const trDiv = document.createElement('div');
      trDiv.classList.add('treatments-form-clinic-treatment');
      trDiv.innerHTML = treatment.getShortName();
      treatmentsDiv.appendChild(trDiv);
    });

    return div;
  }

  initFilter ()
  {
    /*app.clinics.getClinics({
      withPrices: true
    }).then(clinics => {
      clinics = clinics.filter(clinic => clinic.isPresentation());

      let lowestPrice = 9999999999;
      let highestPrice = 0;
      const container = this.querySelector('.treatments-form-clinics');
      container.innerHTML = '';

      clinics.forEach(clinic => {
        const div = this.createClinicElement(clinic);
        container.appendChild(div);

        clinic.getTreatments().forEach(treatment => {
          if (treatment.getLocalPrice() > highestPrice) {
            highestPrice = treatment.getLocalPrice();
          }

          if (treatment.getLocalPrice() < lowestPrice) {
            lowestPrice = treatment.getLocalPrice();
          }
        });
      });

      this.setPriceMinMax(lowestPrice, highestPrice);
    });*/
  }

  filter ()
  {
    const typeInput = this.querySelector('[name="treatment_type"]:checked');
    const type = typeInput ? typeInput.value : null;

    if (!type) {
      this.filteredResults = [];
      this.refreshMap();

      return;
    }

    app.clinics.getClinics({
      withPrices: true
    }).then(clinics => {
      let rearrange = false;

      this.filteredResults = [];

      const container = this.querySelector('.treatments-form-clinics');

      clinics = clinics.filter(clinic => clinic.isPresentation());

      clinics.forEach(clinic => {
        if (!clinic.currentInBounds) {
          return;
        }

        const treatments = this.getFilteredTreatments(clinic, true);

        if (treatments.length > 0) {
          this.filteredResults.push({
            clinic: clinic,
            treatments: treatments
          });
        }
      });

      this.filteredResults = this.filteredResults.sort((a, b) => {
        if (!a.clinic.getDistance().value) {
          return 1;
        } else if (!b.clinic.getDistance().value) {
          return -1;
        }

        return a.clinic.getDistance().value === b.clinic.getDistance.value
          ? 0
          : a.clinic.getDistance().value > b.clinic.getDistance().value
            ? 1
            : -1;
      });

      if (this.lastFilteredResults && this.lastFilteredResults.length === this.filteredResults.length) {
        this.lastFilteredResults.forEach((last, index) => {
          if (this.filteredResults[index].clinic.getId() !== last.clinic.getId()) {
            rearrange = true;
            console.log('index', index);
            console.log('last.clinic.getId()', last.clinic.getId());
            console.log('this.filteredResults[index].clinic.getId()', this.filteredResults[index].clinic.getId());
          }
        });
      } else {
        rearrange = true;
      }

      if (rearrange) {
        this.debugger.log('[Treatments form] Results changed, rearrange');
      }

      this.filteredResults.forEach(result => {
        const div = this.getClinicElement(result.clinic, result.treatments);
        div.classList.add('show');

        if (rearrange) {
          // move to bottom
          div.parentNode.appendChild(div);
        }
      });

      this.lastFilteredResults = [].concat(this.filteredResults);

      container.querySelectorAll('.treatments-form-clinic').forEach(div => {
        if (!this.filteredResults.find(result => result.clinic.getId() == div.dataset.clinicId)) {
          div.classList.remove('show');
        }
      });

      this.debugger.log('[Treatments form] Filtered clinics', this.filteredResults);

      this.refreshMap();
      this.refreshStep();
    });
  }

  getFilteredTreatments (clinic, allTypes = false)
  {
    const typeInput = this.querySelector('[name="treatment_type"]:checked');
    const type = typeInput ? typeInput.value : null;
    const range = this.priceRange.range();
    const homeClinicId = this.querySelector('[name="donor[clinic_id]"]')
      ? this.querySelector('[name="donor[clinic_id]"]').value
      : null;
    const isHomeClinic = homeClinicId == clinic.getShopifyId();
    const result = [];

    clinic.getTreatments().forEach(treatment => {
      if (treatment.getLocalPrice() >= range.start
        && treatment.getLocalPrice() <= range.end
        && (treatment.getType() === type || allTypes)
        && (
          (isHomeClinic && treatment.isForOwnDonor())
          || (!isHomeClinic && !treatment.isForOwnDonor())
        )
      ) {
        result.push(treatment);
      } else {
        console.log('not pushing', treatment);
        console.log('treatment.getType()', treatment.getType());
        console.log('treatment.getLocalPrice()', treatment.getLocalPrice());
        console.log('treatment.isForOwnDonor()', treatment.isForOwnDonor());
        console.log('isHomeClinic', isHomeClinic);
      }
    });

    return result;
  }

  refreshMap ()
  {
    if (!this.map) {
      return;
    }

    Object.keys(this.mapMarkersByClinicId).forEach(clinicId => {
      const clinic = this.filteredResults.find(result => result.clinic.getId() == clinicId);

      if (clinic) {
        this.mapMarkersByClinicId[clinicId].map = this.map;
      } else {
        this.mapMarkersByClinicId[clinicId].map = null;
      }
    });
  }

  refreshTreatments ()
  {
    const clinicSelected = this.querySelector('[name="treatment[clinic_id]"]:checked');

    if (!clinicSelected) {
      return;
    }

    const container = this.querySelector('.treatments-form-treatment-choice-list');
    container.innerHTML = '';

    this.querySelector('[data-treatment-choice-subtotal]').innerHTML = app.money(0);

    app.clinics.getById(clinicSelected.value).then(clinic => {
      this.querySelector('[data-clinic-info-title]').innerHTML = clinic.getName();
      this.querySelector('[data-clinic-info-address]').innerHTML = clinic.getAddressString();
      this.querySelector('[data-clinic-info-country]').innerHTML = clinic.getCountryString();
      this.querySelector('[data-clinic-info-route]').href = clinic.getRouteLink();

      const treatments = this.getFilteredTreatments(clinic);

      this.debugger.log('[Treatments form] Filtered treatments', treatments);

      const recalcTotal = () => {
        let total = 0;

        container.querySelectorAll('input:checked').forEach(checkbox => {
          const treatment = treatments.find(t => t.getId() == checkbox.value);

          if (treatment) {
            total += treatment.getLocalPrice();
          } else {
            console.error('Could not find treatment #' + checkbox.value + ' for checkbox', checkbox);
          }
        });

        this.querySelector('[data-treatment-choice-subtotal]').innerHTML = app.money(total);
      }

      treatments.forEach(treatment => {
        const checkbox = document.createElement('div');

        checkbox.classList.add('treatments-form-treatment-choice-item');
        checkbox.innerHTML = `
          <label class="custom-checkbox" for="treatments-form-treatment-choice-item-${treatment.getId()}">
            <input type="checkbox" name="treatment_ids[]" value="${treatment.getId()}" id="treatments-form-treatment-choice-item-${treatment.getId()}" >
            <span class="custom-checkbox-status"></span>
            <span class="custom-checkbox-label">
              <span class="treatments-form-treatment-choice-item-name">${treatment.getVariantName()}</span>
              <span class="treatments-form-treatment-choice-item-price">${treatment.getLocalPriceFormatted()}</span>
            </span>
          </label>
        `;

        container.appendChild(checkbox);

        checkbox.querySelector('input').addEventListener('change', e => {
          recalcTotal();

          this.checkSelectedTreatments();
          this.refreshStep();
        });
      });

      recalcTotal();
    });
  }

  checkSelectedTreatments ()
  {
    this.selectedTreatments = [];

    if (!this.selectedClinic) {
      return;
    }

    this.querySelectorAll('.treatments-form-treatment-choice-item input:checked').forEach(input => {
      this.selectedClinic.getTreatments().forEach(treatment => {
        if (treatment.getId() == input.value) {
          this.selectedTreatments.push(treatment);
        }
      });
    })
  }

  recalcInBounds ()
  {
    if (!this.map || !this.map.getBounds()) {
      return;
    }

    app.clinics.getMapMarkers().then(markers => {
      markers.forEach(data => {
        data.clinic.currentInBounds = this.map.getBounds().contains(data.marker.position);
      });
    });
  }

  recalcDistance ()
  {
    if (!this.map) {
      return;
    }

    const service = new google.maps.DistanceMatrixService();
    const origin = new google.maps.LatLng(this.customerLocation.latitude, this.customerLocation.longitude);

    app.clinics.getMapMarkers().then(markers => {
      const clinicMarkers = [];
      const destinations = [];

      markers.forEach(data => {
        clinicMarkers.push(data);
        destinations.push(new google.maps.LatLng(data.marker.position.lat, data.marker.position.lng));
      });

      service.getDistanceMatrix({
        origins: [origin],
        destinations: destinations,
        travelMode: 'DRIVING',
        unitSystem: google.maps.UnitSystem.METRIC,
      }, (response, status) => {
        if (status != google.maps.DistanceMatrixStatus.OK) {
          return console.error('Failed to get distance matrix');
        }

        response.rows[0].elements.forEach((el, index) => {
          const clinic = clinicMarkers[index].clinic;

          if (el.distance) {
            clinic.setDistance(el.distance);
          } else {
            console.error(`Could not get distance for "${clinic.getName()}" #${clinic.getShopifyId()}: ${clinic.getLatitude()},${clinic.getLongitude()}`);

            clinic.setDistance({
              value: null,
              text: ''
            });
          }
        });

        this.filter();
      });
    });
  }

  submit ()
  {
    const json = {
      donorId: this.querySelector('[name="donor[id]"]').value,
      clinicId: this.selectedClinic.getShopifyId(),
      treatmentIds: this.selectedTreatments.map(treatment => treatment.getShopifyId())
    };

    app.cache.put('treatments_selected_' + this.querySelector('[name="donor[id]"]').value, json, this.cacheTime);

    broadcast('treatments_selected', {
      donorId: this.querySelector('[name="donor[id]"]').value,
      clinic: this.selectedClinic,
      treatments: this.selectedTreatments
    });
  }

  loadPrevious ()
  {
    const json = app.cache.get('treatments_selected_' + this.querySelector('[name="donor[id]"]').value);

    if (json) {
      app.clinics.getByShopifyId(json.clinicId, {
        withPrices: true
      }).then(clinic => {
        const data = {
          donorId: json.donorId,
          clinic: clinic,
          treatments: clinic.getTreatments().filter(treatment => json.treatmentIds.includes(treatment.getShopifyId()))
        };

        broadcast('treatments_selected', data);
      });
    }
  }
}

function initTreatmentsMap() {
  broadcast('google_maps_initialized');
}

if (!customElements.get('treatments-form')) {
  customElements.define('treatments-form', TreatmentsForm);
}